import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { useFeatureFlag } from "../hooks/useFeatureFlag";

const Navbar = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const showSocial = useFeatureFlag("show-social-page");
  const showPlants = useFeatureFlag("show-plants-page");
  const showSupplies = useFeatureFlag("show-supplies-page");

  const handleNavClick = (label) => {
    ReactGA.event({
      category: "Navigation",
      action: "Click Nav Link",
      label: `${label} - From ${location.pathname}`,
    });
    setMobileMenuOpen(false);
  };

  const allLinks = [
    { path: "/", label: "Fish", emoji: "🐠", key: "prices" },
    { path: "/inverts", label: "Inverts", emoji: "🦐", key: "inverts" },
    { path: "/plants", label: "Plants", emoji: "🌿", key: "plants" },
    { path: "/supplies", label: "Supplies", emoji: "🧰", key: "supplies" },
    { path: "/shopping-assistant", label: "Shopping Lists", emoji: "🛒", key: "shoppingAssistant" },
    { path: "/articles", label: "Care Guides", emoji: "📖", key: "articles" },
    { path: "/reefcyclopedia", label: "Reefcyclopedia", emoji: "🐙", key: "reefcyclopedia" },
    { path: "/notifications", label: "Alerts", emoji: "🔔", key: "notifications" },
    { path: "/classifieds", label: "Classifieds", emoji: "📢", key: "classifieds" },
    { path: "/reviews", label: "Reviews", emoji: "⭐", key: "reviews" },
    { path: "/newsletter", label: "Newsletter", emoji: "📧", key: "newsletter" },
    { path: "/social", label: "Social", emoji: "💬", key: "social" },
    { path: "/videos", label: "Videos", emoji: "🎬", key: "videos" },
    { path: "/reef-wizard", label: "Reef Wizard", emoji: "🧙‍♂️", key: "reefWizard" },
  ];

  const links = allLinks.filter((link) => {
    if (link.key === "social" && !showSocial) return false;
    if (link.key === "plants" && !showPlants) return false;
    if (link.key === "supplies" && !showSupplies) return false;
    return true;
  });

  const isActive = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <nav className="mb-4">
      {/* Desktop nav */}
      <div className="navbar-desktop bg-white backdrop-blur rounded-lg shadow border border-gray-200 px-2 py-1">
        <div className="flex items-center justify-center flex-wrap gap-0.5">
          {links.map((link) => (
            <Link
              key={link.key}
              to={link.path}
              onClick={() => handleNavClick(link.label)}
              data-posthog={`nav-${link.key}`}
              className={`px-2 py-1 rounded-md text-xs font-medium transition-colors duration-150 whitespace-nowrap ${
                isActive(link.path)
                  ? "bg-blue-500 text-white shadow-sm"
                  : "text-gray-600 hover:bg-blue-50 hover:text-blue-600"
              }`}
            >
              <span className="mr-1">{link.emoji}</span>
              {link.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Mobile hamburger */}
      <div className="navbar-mobile">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-full bg-white backdrop-blur rounded-lg shadow border border-gray-200 px-3 py-2 flex items-center justify-between"
          aria-label="Toggle navigation menu"
        >
          <span className="text-sm font-semibold text-gray-700">
            {links.find((l) => isActive(l.path))?.emoji}{" "}
            {links.find((l) => isActive(l.path))?.label || "Menu"}
          </span>
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${mobileMenuOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {mobileMenuOpen && (
          <div className="mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            {links.map((link) => (
              <Link
                key={link.key}
                to={link.path}
                onClick={() => handleNavClick(link.label)}
                data-posthog={`nav-${link.key}`}
                className={`block px-3 py-2 text-sm font-medium transition-colors duration-150 border-b border-gray-100 last:border-b-0 ${
                  isActive(link.path)
                    ? "bg-blue-50 text-blue-600"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <span className="mr-2">{link.emoji}</span>
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
